import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const TOURNAMENT_NAMES = {
  freeroll: "Daily Freeroll",
  daily_cup: "Daily Cup",
  weekly_crown: "Weekly Crown",
  high_roller: "High Roller",
  whale_table: "Whale Table",
};
import Board from "../components/Board";
import PlayerHUD from "../components/PlayerHUD";
import CardView from "../components/Card";
import { CardBack } from "../components/CardBack";
import HowToPlay from "../components/HowToPlay";
import ResultModal from "../components/ResultModal";
import ChatPanel from "../components/ChatPanel";
import {
  PLAYERS, legalNormalMoves, legalSpecialTargets, BOARD_DIM, SPECIAL_LABEL, SPECIAL_DESC,
} from "../lib/quattro";
import { fetchOrCreateProfile, getStoredName, getOrCreateUserId, API } from "../lib/api";
import { sfx, isMuted, setMuted } from "../lib/sound";
import { hapticLight, hapticMedium, hapticSuccess, hapticError, vibrate } from "../lib/native";
import { QuattroSocket } from "../lib/wsClient";
import axios from "axios";

const BET = 100;

// Convert server public state (no decks contents, deckCounts only) into a pseudo-state our existing
// engine helpers expect (legalNormalMoves/legalSpecialTargets need a state with .board + .drawnCard).
function adaptStateForLegality(serverState) {
  return {
    board: serverState.board,
    decks: serverState.deckCounts.map(() => []), // not used for legality
    drawnCard: serverState.drawnCard && !serverState.drawnCard.hidden ? serverState.drawnCard : null,
    currentPlayer: serverState.currentPlayer,
    winner: serverState.winner,
    winLine: serverState.winLine,
  };
}

export default function OnlineGame() {
  const nav = useNavigate();
  const { code } = useParams();
  const userId = getOrCreateUserId();

  const [profile, setProfile] = useState(null);
  const [room, setRoom] = useState(null);
  const [serverState, setServerState] = useState(null); // public_view from server
  const [mySeat, setMySeat] = useState(null);
  const mySeatRef = useRef(null);
  const [howto, setHowto] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [chipsDelta, setChipsDelta] = useState(0);
  const [gemsDelta, setGemsDelta] = useState(0);
  const [tournamentId, setTournamentId] = useState(null);
  const [recorded, setRecorded] = useState(false);
  const [mutedState, setMutedState] = useState(isMuted());
  const [chatMessages, setChatMessages] = useState([]);
  const [turnEndsAt, setTurnEndsAt] = useState(null); // server timestamp (seconds since epoch)
  const [serverNowOffset, setServerNowOffset] = useState(0); // serverTime - clientTime
  const [now, setNow] = useState(Date.now() / 1000);
  const [roundCompleteToast, setRoundCompleteToast] = useState(null);
  const [seriesPanelOpen, setSeriesPanelOpen] = useState(false);
  // Only open ResultModal once we've received a definitive gameResult from
  // the server. In series tournaments, the first N-1 rounds end with a
  // winner but the series continues — we must NOT show the result modal
  // on those intermediate rounds, only on the final settle.
  const [gameResultReceived, setGameResultReceived] = useState(false);
  const sockRef = useRef(null);
  const lastTurnRef = useRef(null);
  const lastDrawIdRef = useRef(null);
  const prevStateRef = useRef(null);

  // Load profile
  useEffect(() => {
    (async () => {
      try { setProfile(await fetchOrCreateProfile(getStoredName())); } catch (e) { console.error(e); }
    })();
  }, []);

  // Connect WS
  useEffect(() => {
    if (!profile || !code) return;
    const s = new QuattroSocket({
      code, user_id: userId, name: profile.name, avatar: profile.avatar || "default",
    });
    sockRef.current = s;
    const unsub = s.subscribe((msg) => {
      if (msg.type === "joined") {
        setMySeat(msg.seat); mySeatRef.current = msg.seat; setRoom(msg.room);
      } else if (msg.type === "roomState") {
        setRoom(msg.room);
        if (msg.room.status === "waiting") {
          // game reset → bounce to room
          nav(`/online/room/${msg.room.code}`);
        }
      } else if (msg.type === "gameState") {
        setServerState(msg.state);
        if (msg.turnEndsAt != null) {
          setTurnEndsAt(msg.turnEndsAt);
          // Sync local clock to server clock so countdown is accurate even with drift
          const clientNow = Date.now() / 1000;
          // Estimate server "now" as turnEndsAt - turnLimitSec. If we know turnLimitSec we
          // can correct clock skew; otherwise just trust the server value as-is.
          if (msg.turnLimitSec) {
            const serverNow = msg.turnEndsAt - msg.turnLimitSec;
            setServerNowOffset(serverNow - clientNow);
          }
        } else {
          setTurnEndsAt(null);
        }
      } else if (msg.type === "chat") {
        setChatMessages((prev) => [...prev, {
          seat: msg.seat, name: msg.name, text: msg.text, ts: msg.ts,
        }].slice(-100));
      } else if (msg.type === "turnTimeout") {
        const baseText = msg.replacedByBot
          ? `🤖 ${msg.name} timed out 3× and was replaced by a bot.`
          : `⏰ ${msg.name} timed out (${msg.strikes ?? 1}/3) — auto-played.`;
        setChatMessages((prev) => [...prev, {
          seat: msg.seat, name: "System",
          text: baseText,
          ts: Date.now() / 1000,
          system: true,
        }].slice(-100));
      } else if (msg.type === "gameResult") {
        // Series-final OR standard-game settle. THIS is what legitimately
        // triggers the result modal — never serverState.winner alone, because
        // intermediate rounds in a series also have winner !== null.
        setGameResultReceived(true);
        // Server-authoritative chip delta + pot info.
        // Read seat from ref because the initial mySeat closure value is null —
        // state updates don't rebind this subscriber.
        const seat = mySeatRef.current;
        if (msg.deltas && seat != null) {
          // Python json.dumps coerces int keys → strings, so try both.
          const d = msg.deltas[seat] ?? msg.deltas[String(seat)];
          if (d != null) setChipsDelta(d);
        }
        // Tournament gem bonus (only awarded to winner)
        if (msg.tournamentId && msg.bonusGems > 0 && seat === msg.winnerSeat) {
          setGemsDelta(msg.bonusGems);
        } else {
          setGemsDelta(0);
        }
        setTournamentId(msg.tournamentId || null);
        // Refresh profile to reflect chip+gem changes
        (async () => {
          try {
            const { getProfile } = await import("../lib/api");
            const p = await getProfile();
            setProfile(p);
          } catch (e) { console.error(e); }
        })();
      } else if (msg.type === "roundComplete") {
        // Series tournament: a round ended but the championship continues.
        // Show a brief "Round X · Player wins" toast with the live scoreboard.
        setRoundCompleteToast({
          roundWinnerSeat: msg.roundWinnerSeat,
          seriesScores: msg.seriesScores || {},
          seriesTarget: msg.seriesTarget || 1,
          seriesRound: msg.seriesRound || 0,
        });
        setTimeout(() => setRoundCompleteToast(null), 4500);
      }
    });
    s.connect();
    return () => { unsub(); s.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, code]);

  // 1Hz ticker for turn countdown
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, []);

  // Sound + haptics on transitions (mirror Game.jsx logic but using serverState)
  useEffect(() => {
    if (!serverState) return;
    const prev = prevStateRef.current;
    if (prev) {
      if (serverState.turnCount > prev.turnCount) {
        let placed = false, stacked = false, special = false, blocked = false;
        outer: for (let r = 0; r < BOARD_DIM; r++) for (let c = 0; c < BOARD_DIM; c++) {
          const cur = serverState.board[r][c], pr = prev.board[r][c];
          if (cur.blocked && !pr.blocked) { blocked = true; special = true; break outer; }
          if (cur.stack.length > pr.stack.length) {
            if (pr.stack.length === 0) placed = true; else stacked = true;
            break outer;
          }
          if (cur.stack.length === pr.stack.length && cur.stack.length > 0) {
            const a = cur.stack[cur.stack.length - 1];
            const b = pr.stack[pr.stack.length - 1];
            if (a && b && (a.power !== b.power || a.owner !== b.owner)) { special = true; break outer; }
          }
        }
        if (special) { sfx.special(); hapticMedium(); vibrate(20); }
        else if (stacked) { sfx.stack(); hapticMedium(); vibrate(15); }
        else if (placed) { sfx.place(); hapticLight(); vibrate(8); }
        else if (blocked) { sfx.special(); hapticMedium(); vibrate(20); }
      }
      const drawn = serverState.drawnCard;
      if (drawn) {
        const drawnId = drawn.id || `hidden-p${drawn.owner}-${serverState.turnCount}`;
        if (drawnId !== lastDrawIdRef.current) {
          lastDrawIdRef.current = drawnId;
          sfx.draw();
        }
      }
      if (lastTurnRef.current !== serverState.currentPlayer) {
        lastTurnRef.current = serverState.currentPlayer;
        if (serverState.currentPlayer === mySeat && serverState.winner === null) sfx.turnSelf();
      }
      if (serverState.winner !== null && prev.winner === null) {
        if (serverState.winner === mySeat) {
          sfx.win(); hapticSuccess(); vibrate([0, 60, 40, 60, 40, 120]);
        } else {
          sfx.lose(); hapticError(); vibrate([0, 80, 60, 80]);
        }
      }
    }
    prevStateRef.current = serverState;
  }, [serverState, mySeat]);

  // End-of-game: trigger result modal ONLY after the server sends gameResult.
  // This correctly differentiates intermediate series rounds (roundComplete)
  // from the true final settle (gameResult).
  useEffect(() => {
    if (!gameResultReceived) return;
    if (recorded) return;
    setRecorded(true);
    setTimeout(() => setResultOpen(true), 2600);
  }, [gameResultReceived, recorded]);

  // Compute my legal moves
  const legalSet = useMemo(() => {
    if (!serverState || mySeat == null) return new Set();
    if (serverState.winner !== null) return new Set();
    if (serverState.currentPlayer !== mySeat) return new Set();
    const drawn = serverState.drawnCard;
    if (!drawn || drawn.hidden) return new Set();
    const adapted = adaptStateForLegality(serverState);
    const moves = drawn.special
      ? legalSpecialTargets(adapted, drawn)
      : legalNormalMoves(adapted, drawn);
    return new Set(moves.map(([r, c]) => `${r},${c}`));
  }, [serverState, mySeat]);

  const noLegal = useMemo(() => {
    if (!serverState || mySeat == null) return false;
    if (serverState.currentPlayer !== mySeat) return false;
    if (!serverState.drawnCard || serverState.drawnCard.hidden) return false;
    return legalSet.size === 0;
  }, [serverState, mySeat, legalSet]);

  // Auto-skip if no legal moves
  useEffect(() => {
    if (!noLegal) return;
    const t = setTimeout(() => sockRef.current?.send({ type: "action", action: "skip" }), 1100);
    return () => clearTimeout(t);
  }, [noLegal]);

  const onCellClick = useCallback((r, c) => {
    if (!serverState || mySeat == null) return;
    if (serverState.currentPlayer !== mySeat) return;
    const drawn = serverState.drawnCard;
    if (!drawn || drawn.hidden) return;
    const action = drawn.special ? "useSpecial" : "place";
    sockRef.current?.send({ type: "action", action, r, c });
  }, [serverState, mySeat]);

  const rematch = () => {
    sockRef.current?.send({ type: "rematch" });
    setResultOpen(false);
    setRecorded(false);
    setGameResultReceived(false);
    setServerState(null);
    setChipsDelta(0);
  };

  const toggleMute = () => {
    const next = !mutedState;
    setMuted(next); setMutedState(next);
    if (!next) sfx.click();
  };

  if (!room || !serverState) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center text-white/60">
        <div className="text-center">
          <div className="label-eyebrow mb-2">Connecting…</div>
          <div className="font-mono">{code}</div>
        </div>
      </div>
    );
  }

  // Players for HUD using server room data
  const players = PLAYERS.map((p, i) => {
    const rp = room.players[i];
    return {
      ...p,
      label: rp ? rp.name : `Seat ${i + 1}`,
      level: rp ? (rp.level || 1) : null,
      chips: rp && !rp.isBot ? rp.chips : null,
      isBot: rp ? rp.isBot : false,
    };
  });

  // Build a state shape for Board/HUD
  const stateForBoard = {
    ...serverState,
    decks: serverState.deckCounts.map(() => []),
  };

  const drawn = serverState.drawnCard;
  const isMyTurn = serverState.currentPlayer === mySeat && serverState.winner === null;

  return (
    <div className="min-h-screen w-full px-3 md:px-6 py-4 md:py-6 flex flex-col items-center">
      <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4">
        <div className="lg:col-span-12 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 md:gap-3">
            <button className="btn-ghost" onClick={() => nav("/")} data-testid="online-exit-btn">Exit</button>
            <div className="font-display font-black text-lg md:text-2xl tracking-tight">QUATTRO</div>
            <span className="label-eyebrow hidden md:inline" data-testid="online-room-code">ROOM {room.code}</span>
          </div>
          <div className="flex items-center gap-1.5 md:gap-3 flex-wrap justify-end">
            {/* Persistent SERIES scoreboard pill — visible whenever the room
                is a multi-round tournament. Tap to expand into a full panel. */}
            {room?.seriesTarget > 1 && (
              <button
                onClick={() => { sfx.click(); setSeriesPanelOpen(true); }}
                className="glass rounded-full px-2.5 py-1 md:px-3 md:py-1.5 flex items-center gap-1.5 hover:bg-white/10 transition-colors"
                style={{
                  border: "1px solid rgba(181,55,242,0.45)",
                  boxShadow: "0 0 12px rgba(181,55,242,0.20)",
                  minHeight: 32,
                }}
                data-testid="series-pill"
                title="Tap to view series scoreboard"
              >
                <span className="label-eyebrow" style={{ color: "#B537F2", fontSize: 9 }}>SERIES</span>
                <span className="font-mono font-bold flex items-center gap-1" style={{ fontSize: 11 }}>
                  {[0, 1, 2, 3].map((seat) => {
                    const p = room.players[seat];
                    if (!p) return null;
                    const score = parseInt((room.seriesScores || {})[String(seat)] || 0, 10);
                    return (
                      <span key={seat} style={{ color: PLAYERS[seat]?.color }}>
                        {score}{seat < 3 ? "·" : ""}
                      </span>
                    );
                  })}
                </span>
                <span className="text-white/45" style={{ fontSize: 10 }}>/{room.seriesTarget}</span>
              </button>
            )}
            {profile && (
              <div className="glass rounded-full px-2.5 py-1.5 md:px-4 md:py-2 flex items-center gap-1.5 md:gap-2" data-testid="online-game-chips" style={{ minHeight: 32 }}>
                <span style={{ color: "#FFD700", fontSize: 14 }}>◆</span>
                <span className="font-mono font-bold" style={{ fontSize: 12 }}>{(profile.chips || 0).toLocaleString()}</span>
              </div>
            )}
            <div className="glass rounded-full px-2.5 py-1.5 md:px-4 md:py-2 hidden sm:flex items-center gap-2" style={{ minHeight: 32 }}>
              <span className="label-eyebrow" style={{ fontSize: 10 }}>ANTE</span>
              <span className="font-mono font-bold" style={{ color: "#FFD700", fontSize: 12 }} data-testid="online-ante">◆ {room?.bet || 0}</span>
            </div>
            <div className="glass rounded-full px-2.5 py-1.5 md:px-4 md:py-2 flex items-center gap-1.5 md:gap-2" style={{ minHeight: 32 }}>
              <span className="label-eyebrow" style={{ fontSize: 10 }}>POT</span>
              <span className="font-mono font-bold" style={{ color: "#FFD700", fontSize: 12 }} data-testid="online-pot">◆ {room?.pot || (room?.bet ? room.bet * 4 : 0)}</span>
            </div>
            <button className="glass rounded-full px-2.5 py-1.5 md:px-3 md:py-2 hover:bg-white/10" onClick={toggleMute} data-testid="online-mute-toggle"
              style={{ border: "1px solid rgba(255,255,255,0.12)", minHeight: 32 }}>
              <span style={{ fontSize: 13 }}>{mutedState ? "🔇" : "🔊"}</span>
            </button>
            <button className="btn-ghost" onClick={() => setHowto(true)} data-testid="online-howto-btn">Rules</button>
          </div>
        </div>

        <div className="lg:col-span-12">
          <PlayerHUD
            players={players}
            decks={serverState.deckCounts.map((n) => new Array(n).fill(null))}
            currentPlayer={serverState.currentPlayer}
            drawnCard={serverState.drawnCard}
            humanId={mySeat}
            activeSkins={Object.fromEntries(
              (room?.players || []).map((p, i) => [i, p?.activeSkin || "default"])
            )}
          />
        </div>
        {/* MOBILE-ONLY compact game bar (above board): turn + drawn card + timer */}
        <div className="lg:hidden glass rounded-2xl p-3" data-testid="online-mobile-game-bar">
          <div className="flex items-center gap-3">
            <div className="rounded-lg flex-shrink-0" style={{ width: 56, height: 76 }} data-testid="online-mobile-drawn-card">
              {drawn ? (
                drawn.hidden ? (
                  <CardBack
                    skin={room.players[drawn.owner]?.activeSkin || "default"}
                    color={PLAYERS[drawn.owner].color}
                    suit={PLAYERS[drawn.owner].suit}
                    size="sm"
                  />
                ) : (
                  <CardView card={drawn} glow />
                )
              ) : (
                <div className="w-full h-full rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 text-[9px] label-eyebrow">…</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="rounded-full flex-shrink-0" style={{
                  width: 8, height: 8, background: PLAYERS[serverState.currentPlayer].color,
                  boxShadow: `0 0 8px ${PLAYERS[serverState.currentPlayer].glow}`,
                }} />
                <div className="font-display font-bold text-sm truncate">
                  {room.players[serverState.currentPlayer]?.name || "Waiting..."}
                  {isMyTurn && <span className="text-white/55 text-xs ml-1">· your turn</span>}
                </div>
              </div>
              {drawn?.special && !drawn.hidden ? (
                <div className="text-[11px] mt-1 truncate" style={{ color: PLAYERS[drawn.owner].color }}>
                  {SPECIAL_LABEL[drawn.special]}
                </div>
              ) : (
                isMyTurn && drawn && !drawn.hidden && (
                  <div className="text-[11px] text-white/65 mt-1 truncate">Tap a highlighted cell</div>
                )
              )}
              {noLegal && (
                <div className="text-[11px] mt-1" style={{ color: "#FF8A8A" }}>No legal move…</div>
              )}
            </div>
            {turnEndsAt && serverState.winner === null && (() => {
              const serverNow = now + serverNowOffset;
              const remaining = Math.max(0, Math.ceil(turnEndsAt - serverNow));
              const danger = remaining <= 5;
              return (
                <span
                  className="font-mono font-bold rounded-full px-2 py-1 flex-shrink-0"
                  style={{
                    fontSize: 11,
                    color: danger ? "#FF5C5C" : "#FFD700",
                    background: danger ? "rgba(255,92,92,0.12)" : "rgba(255,215,0,0.10)",
                    border: `1px solid ${danger ? "rgba(255,92,92,0.4)" : "rgba(255,215,0,0.3)"}`,
                    minHeight: 32,
                  }}
                  data-testid="mobile-turn-timer"
                >
                  ⏱ {remaining}s
                </span>
              );
            })()}
          </div>
        </div>

        <div className="lg:col-span-9 flex flex-col items-center">
          <div className="w-full flex justify-center">
            <Board state={stateForBoard} legalSet={legalSet} onCellClick={onCellClick} mode="online" />
          </div>
        </div>

        <div className="lg:col-span-3 hidden lg:flex flex-col gap-4">
          <div className="glass rounded-3xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="label-eyebrow">Current Turn</span>
              {turnEndsAt && serverState.winner === null && (() => {
                const serverNow = now + serverNowOffset;
                const remaining = Math.max(0, Math.ceil(turnEndsAt - serverNow));
                const danger = remaining <= 5;
                return (
                  <span
                    className="font-mono font-bold rounded-full px-2 py-0.5"
                    style={{
                      fontSize: 11,
                      color: danger ? "#FF5C5C" : "#FFD700",
                      background: danger ? "rgba(255,92,92,0.12)" : "rgba(255,215,0,0.10)",
                      border: `1px solid ${danger ? "rgba(255,92,92,0.4)" : "rgba(255,215,0,0.3)"}`,
                    }}
                    data-testid="turn-timer"
                  >
                    ⏱ 00:{remaining.toString().padStart(2, "0")}
                  </span>
                );
              })()}
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-full" style={{
                width: 12, height: 12, background: PLAYERS[serverState.currentPlayer].color,
                boxShadow: `0 0 12px ${PLAYERS[serverState.currentPlayer].glow}`,
              }} />
              <div className="font-display font-bold" data-testid="online-current-turn">
                {room.players[serverState.currentPlayer]?.name || "Waiting..."}
                {isMyTurn && <span className="text-xs text-white/55 ml-2">(your turn)</span>}
              </div>
            </div>
          </div>

          <div className="glass rounded-3xl p-5">
            <div className="label-eyebrow mb-3">Drawn Card</div>
            <AnimatePresence mode="wait">
              {drawn ? (
                drawn.hidden ? (
                  <motion.div
                    key={`hidden-${serverState.turnCount}`}
                    initial={{ rotateY: 90, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }}
                    style={{ width: 120, height: 160 }}
                    data-testid="online-drawn-card-hidden"
                  >
                    <CardBack
                      skin={room.players[drawn.owner]?.activeSkin || "default"}
                      color={PLAYERS[drawn.owner].color}
                      suit={PLAYERS[drawn.owner].suit}
                      size="lg"
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key={drawn.id}
                    initial={{ rotateY: 90, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }}
                    style={{ width: 120, height: 160 }}
                    data-testid="online-drawn-card"
                  >
                    <CardView card={drawn} glow />
                  </motion.div>
                )
              ) : (
                <div style={{ width: 120, height: 160 }} className="rounded-xl bg-white/5 border border-white/10
                  flex items-center justify-center text-white/40 text-xs label-eyebrow">
                  Drawing…
                </div>
              )}
            </AnimatePresence>
            {drawn?.special && !drawn.hidden && (
              <div className="mt-4">
                <div className="label-eyebrow mb-1" style={{ color: PLAYERS[drawn.owner].color }}>
                  {SPECIAL_LABEL[drawn.special]}
                </div>
                <div className="text-xs text-white/70">{SPECIAL_DESC[drawn.special]}</div>
              </div>
            )}
            {isMyTurn && drawn && !drawn.hidden && (
              <div className="mt-4 text-xs text-white/65">
                {drawn.special ? "Pick a highlighted target." : "Click a highlighted cell to place."}
              </div>
            )}
            {noLegal && (
              <div className="mt-4 text-xs" style={{ color: "#FF8A8A" }} data-testid="online-no-legal">
                No legal move — turn passes…
              </div>
            )}
          </div>

          <div className="glass rounded-3xl p-5 scroll-y" style={{ maxHeight: 220 }}>
            <div className="label-eyebrow mb-2">Game Log</div>
            <div className="space-y-1 text-xs font-mono text-white/65" data-testid="online-game-log">
              {(serverState.log || []).slice(-12).reverse().map((l, i) => (<div key={i}>{l}</div>))}
            </div>
          </div>
        </div>

        {/* MOBILE-ONLY game log — collapsed to keep board above the fold */}
        <details className="lg:hidden glass rounded-2xl px-4 py-3" data-testid="online-mobile-log">
          <summary className="cursor-pointer label-eyebrow text-white/65 flex items-center justify-between">
            <span>Game Log</span>
            <span className="text-white/40 text-[10px]">tap to expand</span>
          </summary>
          <div className="mt-3 space-y-1 text-xs font-mono text-white/65 max-h-40 overflow-y-auto">
            {(serverState.log || []).length === 0 && <div>—</div>}
            {(serverState.log || []).slice(-12).reverse().map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>
        </details>
      </div>

      <HowToPlay open={howto} onClose={() => setHowto(false)} />
      <ChatPanel
        messages={chatMessages}
        mySeat={mySeat}
        players={room.players}
        onSend={(text) => sockRef.current?.send({ type: "chat", text })}
      />
      <ResultModal
        open={resultOpen}
        winner={serverState.winner}
        humanId={mySeat}
        chipsDelta={chipsDelta}
        gemsDelta={gemsDelta}
        tournamentId={tournamentId || room?.tournamentId}
        victoryAnim={profile?.active_victory_anim}
        onPlayAgain={rematch}
        onLobby={() => nav("/")}
      />

      {/* Series-tournament round-complete toast — appears for 4.5s after each
          round. Outer wrapper handles centering with flex so Framer Motion's
          y-transform on the inner card can't override translateX(-50%). */}
      {roundCompleteToast && (
        <div
          className="fixed left-0 right-0 top-4 z-[140] flex justify-center px-3 pointer-events-none"
        >
          <motion.div
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            className="glass rounded-2xl px-4 py-3 pointer-events-auto"
            style={{
              border: "1px solid rgba(181,55,242,0.55)",
              boxShadow: "0 0 28px rgba(181,55,242,0.35)",
              width: "100%",
              maxWidth: 360,
            }}
            data-testid="round-complete-toast"
          >
            <div className="label-eyebrow text-center mb-1.5" style={{ color: "#B537F2", fontSize: 9 }}>
              ROUND {roundCompleteToast.seriesRound} COMPLETE
            </div>
            <div className="font-display font-black text-center mb-2.5" style={{ fontSize: 13 }}>
              {roundCompleteToast.roundWinnerSeat != null
                ? `${(room.players[roundCompleteToast.roundWinnerSeat]?.name || "?").split(" ")[0]} wins the round`
                : "Draw"}
            </div>
            <div className="grid grid-cols-4 gap-1.5" data-testid="series-scoreboard">
              {[0, 1, 2, 3].map((seat) => {
                const p = room.players[seat];
                if (!p) return <div key={seat} />;
                const score = parseInt(roundCompleteToast.seriesScores[String(seat)] || 0, 10);
                const target = roundCompleteToast.seriesTarget;
                const isLeader = score >= target;
                return (
                  <div
                    key={seat}
                    className="rounded-xl py-1.5 px-1 text-center"
                    style={{
                      background: isLeader ? `${PLAYERS[seat]?.color}25` : "rgba(255,255,255,0.04)",
                      border: `1px solid ${isLeader ? PLAYERS[seat]?.color : "rgba(255,255,255,0.06)"}`,
                    }}
                  >
                    <div className="font-mono text-white/55 truncate" style={{ fontSize: 9 }}>
                      {(p.name || "?").split(" ")[0].slice(0, 7)}
                    </div>
                    <div className="font-display font-black mt-0.5" style={{ fontSize: 14, color: PLAYERS[seat]?.color }}>
                      {score}/{target}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-center text-white/55 mt-1.5" style={{ fontSize: 10 }}>Next round starting…</div>
          </motion.div>
        </div>
      )}

      {/* Series scoreboard panel — opened on demand via the persistent pill.
          Always-available view of the live championship state. */}
      <AnimatePresence>
        {seriesPanelOpen && room?.seriesTarget > 1 && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] flex items-center justify-center px-4"
            style={{ background: "rgba(0,0,0,0.78)" }}
            onClick={() => setSeriesPanelOpen(false)}
            data-testid="series-panel"
          >
            <motion.div
              initial={{ y: 12, scale: 0.96 }} animate={{ y: 0, scale: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="glass rounded-3xl p-6 w-full"
              style={{
                maxWidth: 380,
                border: "1px solid rgba(181,55,242,0.55)",
                boxShadow: "0 0 28px rgba(181,55,242,0.35)",
              }}
            >
              <div className="label-eyebrow text-center mb-1" style={{ color: "#B537F2" }}>
                BEST OF {room.seriesTarget * 2 - 1}
              </div>
              <h2 className="font-display font-black text-2xl text-center mb-1">
                {TOURNAMENT_NAMES[room.tournamentId] || "Championship"}
              </h2>
              <div className="text-white/55 text-xs text-center mb-5">
                First to <b style={{ color: "#B537F2" }}>{room.seriesTarget} round wins</b> claims the pot
                {room.bonusGems > 0 && <> + ◈ {room.bonusGems} gems</>}
                {room.bonusChips > 0 && <> + ◆ {room.bonusChips.toLocaleString()} chips</>}
              </div>
              <div className="space-y-2">
                {[0, 1, 2, 3].map((seat) => {
                  const p = room.players[seat];
                  if (!p) return null;
                  const score = parseInt((room.seriesScores || {})[String(seat)] || 0, 10);
                  const isLeader = score >= room.seriesTarget;
                  const pct = Math.min(100, (score / room.seriesTarget) * 100);
                  return (
                    <div
                      key={seat}
                      className="rounded-xl p-3"
                      style={{
                        background: isLeader ? `${PLAYERS[seat].color}25` : "rgba(255,255,255,0.04)",
                        border: `1px solid ${isLeader ? PLAYERS[seat].color : "rgba(255,255,255,0.08)"}`,
                      }}
                      data-testid={`series-row-${seat}`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className="rounded-full flex-shrink-0"
                            style={{
                              width: 10, height: 10,
                              background: PLAYERS[seat].color,
                              boxShadow: `0 0 8px ${PLAYERS[seat].glow}`,
                            }}
                          />
                          <div className="font-display font-bold text-sm truncate">
                            {p.name}{seat === mySeat && <span className="text-white/45 text-xs ml-1">(you)</span>}
                          </div>
                          {isLeader && (
                            <span className="label-eyebrow" style={{ color: PLAYERS[seat].color, fontSize: 9 }}>
                              CHAMPION
                            </span>
                          )}
                        </div>
                        <div className="font-mono font-black flex-shrink-0" style={{ fontSize: 16, color: PLAYERS[seat].color }}>
                          {score}/{room.seriesTarget}
                        </div>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                        <div
                          className="h-full transition-all"
                          style={{ width: `${pct}%`, background: PLAYERS[seat].color }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <button
                className="btn-primary w-full mt-5"
                onClick={() => setSeriesPanelOpen(false)}
                data-testid="series-panel-close"
              >Got it</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
